import { Fragment, useCallback, useEffect, useState } from "react";
import "./AdminAdCampaigns.css";
import SponsoredAd from "./SponsoredAd";
import { adminAds, formatCount } from "../../services/adsApi";

// =====================================================
// ADMIN — ADVERTISING
// Rendered as sections of the Admin Dashboard, in its own table style:
//   1. Statistics: campaigns per status, impressions, clicks, CTR and
//      revenue (verified payments only) — all computed by the server.
//   2. Ad types: price, estimated impression range, maximum duration and
//      availability — edited here, no code change or deploy needed.
//   3. Campaigns: the review queue and every campaign. "Review" opens the
//      full campaign (advertiser, creative, website security check,
//      targeting, price, payment, status, reports) with the actions its
//      status allows: Approve · Reject (reason mandatory) · Pause · Resume ·
//      Remove.
//   4. Reported advertisements, most reported first.
// The server enforces every rule; this screen only calls it.
// =====================================================

const STATUS_FILTERS = [
  ["pending_review", "Pending Review"],
  ["reported", "Reported"],
  ["", "All"],
  ["draft", "Draft"],
  ["approved", "Approved"],
  ["payment_pending", "Payment Pending"],
  ["scheduled", "Scheduled"],
  ["active", "Active"],
  ["paused", "Paused"],
  ["completed", "Completed"],
  ["expired", "Expired"],
  ["rejected", "Rejected"],
  ["removed", "Removed"],
  ["cancelled", "Cancelled"],
];

const BADGE = {
  active: "badge-active",
  scheduled: "badge-active",
  rejected: "badge-blocked",
  removed: "badge-blocked",
  cancelled: "badge-blocked",
};

const SECURITY_LABEL = {
  passed: "Passed",
  warning: "Passed with warnings",
  inconclusive: "Could not be fully verified",
  failed: "Failed",
};

const RESULT_ICON = { pass: "✔", warn: "⚠", fail: "✖", error: "?", skipped: "–", info: "ℹ" };

const REASON_LABEL = {
  misleading: "Misleading",
  suspicious_website: "Suspicious website",
  inappropriate: "Inappropriate",
  not_relevant: "Not relevant",
  other: "Other",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const rupees = (n) => `₹${formatCount(n)}`;

// ── 1. STATISTICS ──
function AdStats({ stats }) {
  if (!stats) return null;
  const c = stats.counts || {};
  const cards = [
    ["Total Campaigns", formatCount(stats.totalCampaigns)],
    ["Pending Review", formatCount(c.pending_review)],
    ["Approved", formatCount(c.approved)],
    ["Active", formatCount(c.active)],
    ["Paused", formatCount(c.paused)],
    ["Completed", formatCount(c.completed)],
    ["Expired", formatCount(c.expired)],
    ["Rejected", formatCount(c.rejected)],
    ["Total Impressions", formatCount(stats.totalImpressions)],
    ["Total Clicks", formatCount(stats.totalClicks)],
    ["CTR", `${stats.ctr}%`],
    ["Revenue", rupees(stats.revenue)],
  ];
  return (
    <>
      <div className="admin-stats-row aac-stats" aria-label="Advertising statistics">
        {cards.map(([label, value]) => (
          <div className="admin-stat-card" key={label}>
            <span className="admin-stat-num">{value}</span>
            <span className="admin-stat-label">{label}</span>
          </div>
        ))}
      </div>
      <p className="aac-note">
        Also: {formatCount(c.payment_pending)} payment pending · {formatCount(c.scheduled)} scheduled ·{" "}
        {formatCount(c.draft)} draft · {formatCount(c.removed)} removed · {formatCount(c.cancelled)} cancelled ·{" "}
        {formatCount(stats.openReports)} open report(s) on {formatCount(stats.reportedCampaigns)} campaign(s). Revenue
        counts only payments verified with Razorpay by the server ({formatCount(stats.paidCampaigns)} paid campaigns).
      </p>
    </>
  );
}

// ── 2. AD TYPES ──
function AdTypeRow({ row, onSaved }) {
  const [draft, setDraft] = useState(row);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => setDraft(row), [row]);

  const num = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value === "" ? "" : Number(e.target.value) }));

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      const data = await adminAds.updateType(row.type, {
        price: draft.price,
        minImpressions: draft.minImpressions,
        maxImpressions: draft.maxImpressions,
        maxDurationDays: draft.maxDurationDays,
        enabled: draft.enabled,
      });
      setMsg("Saved");
      onSaved(data.type);
    } catch (err) {
      setMsg(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td>{row.label}</td>
      <td><input className="aac-input" type="number" min={1} value={draft.price} onChange={num("price")} /></td>
      <td><input className="aac-input" type="number" min={1} value={draft.minImpressions} onChange={num("minImpressions")} /></td>
      <td><input className="aac-input" type="number" min={1} value={draft.maxImpressions} onChange={num("maxImpressions")} /></td>
      <td><input className="aac-input" type="number" min={1} value={draft.maxDurationDays} onChange={num("maxDurationDays")} /></td>
      <td>
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
          aria-label={`${row.label} available`}
        />
      </td>
      <td>
        <button className="admin-btn btn-success" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        {msg && <div className="aac-msg">{msg}</div>}
      </td>
    </tr>
  );
}

// ── 3. CAMPAIGN REVIEW ──
function Field({ label, children }) {
  return (
    <div className="aac-field">
      <span className="aac-field-label">{label}</span>
      <span className="aac-field-value">{children}</span>
    </div>
  );
}

function SecurityResult({ check, onRecheck, busy }) {
  return (
    <div className={`aac-security aac-security-${check?.status || "none"}`}>
      <div className="aac-security-head">
        <strong>Security Check: {check ? SECURITY_LABEL[check.status] || check.status : "Not run"}</strong>
        <button type="button" className="admin-btn" disabled={busy} onClick={onRecheck}>
          Re-run check
        </button>
      </div>
      {check ? (
        <>
          <p className="aac-security-summary">{check.summary}</p>
          <ul className="aac-checks">
            {(check.checks || []).map((k) => (
              <li key={k.key} className={`aac-check aac-check-${k.result}`}>
                <span aria-hidden="true">{RESULT_ICON[k.result] || "•"}</span> <strong>{k.label}:</strong> {k.detail}
              </li>
            ))}
          </ul>
          {check.redirects?.length > 0 && (
            <p className="admin-td-muted">Redirects to: {check.redirects.join(" → ")}</p>
          )}
          <p className="admin-td-muted">Checked {fmtDate(check.checkedAt)}. Automated checks cannot detect every malicious website.</p>
        </>
      ) : (
        <p className="admin-td-muted">This campaign was created before website checks existed. Re-run the check before approving.</p>
      )}
    </div>
  );
}

function CampaignDetail({ campaign: c, reports, busy, onAction }) {
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const security = c.securityCheck?.status || "not_run";
  const needsAck = security !== "passed";
  const a = c.analytics;

  return (
    <div className="aac-detail">
      <div className="aac-detail-grid">
        <div>
          <h3 className="aac-detail-heading">Advertiser</h3>
          <Field label="Brand Name">{c.businessName}</Field>
          <Field label="Contact Details">
            {c.contactName} · <a href={`mailto:${c.email}`}>{c.email}</a> · {c.phone}
          </Field>

          <h3 className="aac-detail-heading">Advertisement</h3>
          <Field label="Advertisement Type">{c.adType}</Field>
          <Field label="Title">{c.title}</Field>
          <Field label="Description">{c.description}</Field>
          <Field label="CTA">{c.ctaText}</Field>
          <Field label="Logo">
            {c.logoUrl ? <img className="aac-logo" src={c.logoUrl} alt={`${c.businessName} logo`} /> : "—"}
          </Field>
          {c.adType === "image" && (
            <Field label="Image">
              <a href={c.imageUrl} target="_blank" rel="noopener noreferrer">Open image</a>
            </Field>
          )}
          {c.adType === "video" && (
            <Field label="Video">
              <a href={c.videoUrl} target="_blank" rel="noopener noreferrer">Open video</a>
            </Field>
          )}
          <Field label="Destination Website">
            <a href={c.destinationUrl} target="_blank" rel="noopener noreferrer nofollow">{c.destinationUrl}</a>
          </Field>

          <h3 className="aac-detail-heading">Targeting &amp; Purchase</h3>
          <Field label="Selected Categories">{c.categories.join(", ")}</Field>
          <Field label="Selected Cities">{c.targetCity || "All Cities"}</Field>
          <Field label="Price">{rupees(c.price)}</Field>
          <Field label="Estimated Impressions">
            {formatCount(c.estimatedImpressions.min)}–{formatCount(c.estimatedImpressions.max)} (minimum target{" "}
            {formatCount(c.minImpressionTarget)})
          </Field>
          <Field label="Campaign Duration">
            Up to {c.maxDurationDays} days
            {c.requestedStartAt && ` · requested start ${fmtDate(c.requestedStartAt)}`}
            {c.startedAt && ` · ${fmtDate(c.startedAt)} → ${fmtDate(c.endsAt)}`}
          </Field>
          <Field label="Payment Status">{c.paymentStatus?.label}{c.paidAt && ` · ${fmtDate(c.paidAt)}`}</Field>
          <Field label="Current Campaign Status">
            <span className={`admin-badge ${BADGE[c.status] || ""}`}>{c.statusLabel}</span>
            {c.rejectionReason && ` · Reason: ${c.rejectionReason}`}
            {c.pauseReason && ` · Reason: ${c.pauseReason}`}
            {c.removalReason && ` · Reason: ${c.removalReason}`}
            {c.completionLabel && ` · ${c.completionLabel}`}
          </Field>
          <Field label="Delivery">
            {formatCount(c.deliveredImpressions)} / {formatCount(c.minImpressionTarget)} impressions
            {a && ` · ${formatCount(a.clicks)} clicks · CTR ${a.ctr}%`}
            {c.startedAt && ` · ${c.durationUsedDays} of ${c.maxDurationDays} days`}
          </Field>
        </div>

        <div>
          <h3 className="aac-detail-heading">Advertisement Preview</h3>
          <div className="aac-preview">
            <SponsoredAd ad={c} />
          </div>
          <SecurityResult check={c.securityCheck} busy={busy} onRecheck={() => onAction(c, "recheck")} />
        </div>
      </div>

      {/* ── actions ── */}
      <div className="aac-actions-panel">
        <label className="aac-reason">
          <span className="aac-field-label">
            Reason {c.status === "pending_review" ? "(required to reject; shown to the advertiser)" : "(optional for pause / remove)"}
          </span>
          <textarea
            className="aac-textarea"
            rows={2}
            maxLength={300}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        {c.status === "pending_review" && needsAck && security !== "failed" && (
          <label className="aac-ack">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
            <span>I reviewed the destination website manually ({SECURITY_LABEL[security] || "check not run"}).</span>
          </label>
        )}
        <div className="admin-action-group">
          {c.status === "pending_review" && (
            <>
              <button
                className="admin-btn btn-success"
                disabled={busy || security === "failed" || (needsAck && !ack)}
                title={security === "failed" ? "The website failed the security check" : undefined}
                onClick={() => onAction(c, "approve", { acknowledgeSecurity: needsAck && ack })}
              >
                Approve
              </button>
              <button
                className="admin-btn btn-danger"
                disabled={busy || reason.trim().length < 3}
                title={reason.trim().length < 3 ? "Enter a rejection reason first" : undefined}
                onClick={() => onAction(c, "reject", { reason: reason.trim() })}
              >
                Reject
              </button>
            </>
          )}
          {["active", "scheduled"].includes(c.status) && (
            <button className="admin-btn btn-warn" disabled={busy} onClick={() => onAction(c, "pause", { reason: reason.trim() })}>
              Pause
            </button>
          )}
          {c.status === "paused" && (
            <button className="admin-btn btn-success" disabled={busy} onClick={() => onAction(c, "resume")}>
              Resume
            </button>
          )}
          {c.status !== "removed" && (
            <button className="admin-btn btn-danger" disabled={busy} onClick={() => onAction(c, "remove", { reason: reason.trim() })}>
              Remove
            </button>
          )}
        </div>
        {c.status === "pending_review" && reason.trim().length < 3 && (
          <p className="admin-td-muted">A rejection reason is required before you can reject.</p>
        )}
      </div>

      {/* ── reports ── */}
      <h3 className="aac-detail-heading">Reports ({reports?.length || 0})</h3>
      {!reports ? (
        <p className="admin-td-muted">Loading…</p>
      ) : reports.length === 0 ? (
        <p className="admin-td-muted">No reports.</p>
      ) : (
        <ul className="aac-reports">
          {reports.map((r) => (
            <li key={r.id}>
              <strong>{r.reasonLabel}</strong>
              {r.details && ` — “${r.details}”`}
              <span className="admin-td-muted">
                {" "}· {r.reporter ? `${r.reporter.name} (${r.reporter.email})` : "deleted user"} · {fmtDate(r.createdAt)} · {r.status}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── 4. REPORTED ADS ──
function ReportedAds({ groups, busy, onOpen, onResolve }) {
  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">🚩 Reported Advertisements</h2>
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Advertisement</th>
              <th>Open reports</th>
              <th>Reasons</th>
              <th>Latest</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr><td colSpan={6} className="admin-empty">No open reports</td></tr>
            ) : (
              groups.map((g) => (
                <tr key={g.campaign.id}>
                  <td>
                    <div className="admin-user-name">{g.campaign.title}</div>
                    <div className="admin-td-muted">{g.campaign.businessName}</div>
                  </td>
                  <td>
                    <strong>{g.count}</strong>
                    <div className="admin-td-muted">{g.uniqueReporters} reporter(s) · {g.totals.total} all-time</div>
                  </td>
                  <td className="admin-td-muted">
                    {Object.entries(g.byReason).map(([k, n]) => `${REASON_LABEL[k] || k} ×${n}`).join(", ")}
                  </td>
                  <td className="admin-td-muted">{fmtDate(g.latestAt)}</td>
                  <td><span className={`admin-badge ${BADGE[g.campaign.status] || ""}`}>{g.campaign.statusLabel}</span></td>
                  <td>
                    <div className="admin-action-group">
                      <button className="admin-btn" onClick={() => onOpen(g.campaign.id)}>Review ad</button>
                      <button className="admin-btn btn-success" disabled={busy} onClick={() => onResolve(g.campaign.id, "reviewed")}>
                        Mark reviewed
                      </button>
                      <button className="admin-btn" disabled={busy} onClick={() => onResolve(g.campaign.id, "dismissed")}>
                        Dismiss
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="aac-note">Reports never remove an advertisement automatically. Review the ad, then pause or remove it if needed.</p>
    </div>
  );
}

// initialStatus: the filter the campaign list opens on ("" = All). The
// dashboard section opens on the review queue; the /ads-admin page on All.
function AdminAdCampaigns({ initialStatus = "pending_review" }) {
  const [stats, setStats] = useState(null);
  const [statsFailed, setStatsFailed] = useState(false);
  const [types, setTypes] = useState([]);
  const [status, setStatus] = useState(initialStatus);
  const [campaigns, setCampaigns] = useState([]);
  const [reported, setReported] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null); // { campaign, reports }
  const [busyId, setBusyId] = useState(null);

  const loadStats = useCallback(() => {
    // A sleeping backend can drop the first request: say so instead of
    // leaving the overview silently empty.
    adminAds
      .stats()
      .then((d) => {
        setStats(d.stats);
        setStatsFailed(false);
      })
      .catch(() => setStatsFailed(true));
    adminAds.reports("open").then((d) => setReported(d.reported || [])).catch(() => {});
  }, []);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await adminAds.listCampaigns(status);
      setCampaigns(data.campaigns || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to load campaigns");
    } finally {
      setLoading(false);
    }
  }, [status]);

  const loadDetail = useCallback(async (id) => {
    setDetail(null);
    try {
      const data = await adminAds.getCampaign(id);
      setDetail({ campaign: data.campaign, reports: data.reports || [] });
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to load the campaign");
    }
  }, []);

  useEffect(() => {
    adminAds.getTypes().then((d) => setTypes(d.types || [])).catch(() => {});
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    if (openId) loadDetail(openId);
    else setDetail(null);
  }, [openId, loadDetail]);

  const refreshAll = async (id) => {
    await loadCampaigns();
    loadStats();
    if (id && openId === id) await loadDetail(id);
  };

  const act = async (campaign, action, payload = {}) => {
    if (action === "remove" && !window.confirm(`Remove "${campaign.title}"? It will never be shown again.`)) return;
    if (action === "approve" && !window.confirm(`Approve "${campaign.title}"? The advertiser will be able to pay for it.`)) return;
    setBusyId(campaign.id);
    setMessage("");
    try {
      let data;
      if (action === "approve") data = await adminAds.approve(campaign.id, { acknowledgeSecurity: payload.acknowledgeSecurity });
      else if (action === "reject") data = await adminAds.reject(campaign.id, payload.reason);
      else if (action === "pause") data = await adminAds.pause(campaign.id, payload.reason);
      else if (action === "resume") data = await adminAds.resume(campaign.id);
      else if (action === "remove") data = await adminAds.remove(campaign.id, payload.reason);
      else if (action === "recheck") data = await adminAds.recheckSecurity(campaign.id);
      setMessage(data?.message || (action === "recheck" ? "Security check updated" : "Done"));
    } catch (err) {
      setMessage(err?.response?.data?.message || "Action failed");
    } finally {
      setBusyId(null);
      await refreshAll(campaign.id);
    }
  };

  const resolveReports = async (campaignId, resolution) => {
    setBusyId(campaignId);
    try {
      await adminAds.reviewReports(campaignId, resolution);
      setMessage(resolution === "dismissed" ? "Reports dismissed" : "Reports marked as reviewed");
    } catch (err) {
      setMessage(err?.response?.data?.message || "Action failed");
    } finally {
      setBusyId(null);
      await refreshAll(campaignId);
    }
  };

  const openFromReports = (id) => {
    setStatus("reported");
    setOpenId(id);
  };

  return (
    <>
      {/* ══ STATISTICS ══ */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2 className="admin-section-title">📊 Advertising Overview</h2>
          <button className="admin-btn" onClick={() => refreshAll(openId)}>Refresh</button>
        </div>
        {stats ? (
          <AdStats stats={stats} />
        ) : (
          <p className="aac-msg" role="status">
            {statsFailed ? "Could not load the statistics. Please press Refresh." : "Loading…"}
          </p>
        )}
      </div>

      {/* ══ AD TYPES ══ */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2 className="admin-section-title">📢 Advertisement Types &amp; Pricing</h2>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Price (₹)</th>
                <th>Min est. impressions</th>
                <th>Max est. impressions</th>
                <th>Max duration (days)</th>
                <th>Available</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <AdTypeRow
                  key={t.type}
                  row={t}
                  onSaved={(updated) => setTypes((all) => all.map((x) => (x.type === updated.type ? updated : x)))}
                />
              ))}
            </tbody>
          </table>
        </div>
        <p className="aac-note">
          Changes apply to campaigns created from now on. Existing campaigns keep the price, target and duration they were bought with.
        </p>
      </div>

      {/* ══ CAMPAIGNS ══ */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2 className="admin-section-title">📢 Advertising Campaigns</h2>
          <select
            className="admin-search aac-filter"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOpenId(null);
            }}
            aria-label="Filter campaigns by status"
          >
            {STATUS_FILTERS.map(([value, label]) => (
              <option key={value || "all"} value={value}>{label}</option>
            ))}
          </select>
        </div>
        {error && <p className="aac-msg">{error}</p>}
        {message && <p className="aac-msg" role="status">{message}</p>}
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Advertiser</th>
                <th>Ad</th>
                <th>Targeting</th>
                <th>Delivered / Target</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="admin-empty">Loading…</td></tr>
              ) : campaigns.length === 0 ? (
                <tr><td colSpan={7} className="admin-empty">No campaigns</td></tr>
              ) : (
                campaigns.map((c, i) => (
                  <Fragment key={c.id}>
                    <tr>
                      <td className="admin-td-num">{i + 1}</td>
                      <td>
                        <div className="admin-user-name">{c.businessName}</div>
                        <div className="admin-td-muted">{c.contactName} · {c.email} · {c.phone}</div>
                      </td>
                      <td>
                        <div>{c.title}</div>
                        <div className="admin-td-muted">
                          {c.adType} · ₹{c.price} · <a href={c.destinationUrl} target="_blank" rel="noopener noreferrer nofollow">{c.destinationUrl}</a>
                        </div>
                        <div className="admin-td-muted">
                          Website check: {c.securityCheck ? SECURITY_LABEL[c.securityCheck.status] : "Not run"}
                          {c.reports?.open > 0 && <span className="aac-flag"> · 🚩 {c.reports.open} open report(s)</span>}
                        </div>
                      </td>
                      <td className="admin-td-muted">
                        {c.categories.join(", ")}
                        <br />
                        {c.targetCity || "All Cities"}
                      </td>
                      <td className="admin-td-muted">
                        {formatCount(c.deliveredImpressions)} / {formatCount(c.minImpressionTarget)}
                        <br />
                        {c.startedAt ? `${c.durationUsedDays} of ${c.maxDurationDays} days` : `up to ${c.maxDurationDays} days`}
                      </td>
                      <td>
                        <span className={`admin-badge ${BADGE[c.status] || ""}`}>{c.statusLabel}</span>
                        <div className="admin-td-muted">{c.paymentStatus?.label}</div>
                        {c.rejectionReason && <div className="admin-td-muted">{c.rejectionReason}</div>}
                      </td>
                      <td>
                        <button className="admin-btn" onClick={() => setOpenId(openId === c.id ? null : c.id)}>
                          {openId === c.id ? "Close" : "Review"}
                        </button>
                      </td>
                    </tr>
                    {openId === c.id && (
                      <tr>
                        <td colSpan={7}>
                          {detail?.campaign?.id === c.id ? (
                            <CampaignDetail
                              key={`${c.id}-${detail.campaign.status}`}
                              campaign={detail.campaign}
                              reports={detail.reports}
                              busy={busyId === c.id}
                              onAction={act}
                            />
                          ) : (
                            <p className="admin-empty">Loading…</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ══ REPORTED ══ */}
      <ReportedAds groups={reported} busy={Boolean(busyId)} onOpen={openFromReports} onResolve={resolveReports} />
    </>
  );
}

export default AdminAdCampaigns;
